/**
 * UploadZone Component
 * Drag-and-drop file upload zone with keyboard accessibility
 */
import { useState, useRef, useCallback } from 'react';
import { cn } from '@/utils/cn';
import Button from '@/components/ui/Button';

export interface UploadZoneProps {
  /** Callback when a file is selected */
  onFileSelect: (file: File) => void;
  /** Maximum file size in megabytes */
  maxSizeMb: number;
  /** List of supported file extensions (for display) */
  supportedTypes: readonly string[];
  /** Whether the upload zone is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
  /**
   * Whether PDF uploads require consent before proceeding.
   * When true, PDF files will not trigger onFileSelect until consent is given.
   * CSV/OFX files are always allowed as they are processed locally.
   */
  requiresPdfConsent?: boolean;
}

/**
 * Drag-and-drop file upload zone with click-to-browse fallback.
 * Fully accessible with keyboard navigation and screen reader support.
 */
export function UploadZone({
  onFileSelect,
  maxSizeMb,
  supportedTypes,
  disabled = false,
  className,
  requiresPdfConsent = false,
}: UploadZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Check if a file is a PDF based on MIME type or extension
   */
  const isPdfFile = useCallback((file: File): boolean => {
    return (
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf')
    );
  }, []);

  /**
   * Handle file selection with PDF consent check
   */
  const handleFileWithConsentCheck = useCallback(
    (file: File) => {
      // If PDF consent is required and this is a PDF, block the upload
      if (requiresPdfConsent && isPdfFile(file)) {
        // Don't proceed - user needs to check the consent box first
        return;
      }
      onFileSelect(file);
    },
    [requiresPdfConsent, isPdfFile, onFileSelect]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileWithConsentCheck(file);
      }
    },
    [disabled, handleFileWithConsentCheck]
  );

  const handleClick = useCallback(() => {
    if (!disabled) {
      inputRef.current?.click();
    }
  }, [disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileWithConsentCheck(file);
        // Reset the input so the same file can be selected again
        e.target.value = '';
      }
    },
    [handleFileWithConsentCheck]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  // Format supported types for display
  const displayTypes = supportedTypes
    .map((ext) => ext.replace('.', '').toUpperCase())
    .join(', ');

  return (
    <div
      className={cn(
        'relative border-2 border-dashed rounded-lg p-8 sm:p-12 text-center transition-colors cursor-pointer',
        isDragOver
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50',
        disabled && 'opacity-50 cursor-not-allowed hover:border-gray-300 hover:bg-transparent',
        className
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="Upload document. Press Enter to select a document or drag and drop a file here. One document at a time."
      aria-disabled={disabled}
    >
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept={supportedTypes.join(',')}
        onChange={handleInputChange}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Document icon */}
      <DocumentIcon
        className={cn(
          'mx-auto h-12 w-12 transition-colors',
          isDragOver ? 'text-primary-500' : 'text-gray-400'
        )}
      />

      {/* Main text */}
      <p className="mt-4 text-lg font-medium text-gray-900">
        {isDragOver ? 'Drop file to upload' : 'Drag and drop your file here'}
      </p>

      <p className="mt-1 text-sm text-gray-500">or</p>

      {/* Browse button */}
      <Button
        type="button"
        variant="outline"
        className="mt-2"
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      >
        Select Document
      </Button>

      {/* Supported types info */}
      <p className="mt-4 text-xs text-gray-500">Supported: {displayTypes}</p>
      <p className="text-xs text-gray-500">Maximum file size: {maxSizeMb} MB</p>
      <p className="mt-2 text-xs text-gray-400">
        Upload one document at a time. You can upload additional documents after processing.
      </p>

      {/* Data handling notice */}
      <div
        className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-md"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-xs text-blue-800">
          <span className="font-semibold">Data Handling Notice:</span> PDF documents
          are processed by Google Gemini AI for data extraction. Your financial
          data is subject to{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-blue-900"
            onClick={(e) => e.stopPropagation()}
          >
            Google&apos;s Privacy Policy
          </a>
          . CSV and OFX files are processed locally without external services.
        </p>
      </div>
    </div>
  );
}

/**
 * Document icon component
 */
function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
      />
    </svg>
  );
}

export default UploadZone;
